import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Truck, ShieldCheck } from "lucide-react";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { itemKey, itemPrice, itemImage } from "@/context/CartContext";
import { toast } from "sonner";

const COUNTRIES = [
    ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"], ["AU", "Australia"],
    ["NZ", "New Zealand"], ["IE", "Ireland"], ["DE", "Germany"], ["FR", "France"], ["NL", "Netherlands"],
];

const EMPTY = { name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" };

export default function CheckoutPage() {
    const { items, total } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [rates, setRates] = useState([]);
    const [selectedRate, setSelectedRate] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const [paying, setPaying] = useState(false);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setRates([]);
        setSelectedRate(null);
    };

    if (items.length === 0) {
        return (
            <main data-testid="checkout-empty" className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-start justify-center px-6 py-24 lg:px-10">
                <p className="font-serif text-3xl italic text-clay">Your bag is empty.</p>
                <Link to="/shop" data-testid="checkout-empty-shop-link" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:text-gold">
                    <ArrowLeft size={14} /> Back to the collection
                </Link>
            </main>
        );
    }

    const addressValid = form.name && form.street1 && form.city && form.state && form.zip;

    const getRates = async () => {
        if (!addressValid) {
            toast.error("Please fill in your name and full address first");
            return;
        }
        setLoadingRates(true);
        try {
            const res = await api.post("/shipping/rates", {
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, variant_id: i.variant?.id || undefined })),
                address_to: { ...form, street2: form.street2 || undefined },
            });
            setRates(res.data.rates);
            setSelectedRate(res.data.rates[0] || null);
            if (res.data.rates.length === 0) toast.error("No USPS rates found for this address");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoadingRates(false);
        }
    };

    const pay = async () => {
        if (!selectedRate) {
            toast.error("Choose a shipping option first");
            return;
        }
        setPaying(true);
        try {
            const res = await api.post("/payments/checkout", {
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, variant_id: i.variant?.id || undefined })),
                origin_url: window.location.origin,
                address_to: { ...form, street2: form.street2 || undefined },
                rate_id: selectedRate.id,
                service: selectedRate.service,
            });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setPaying(false);
        }
    };

    const grand = total + (selectedRate ? selectedRate.amount : 0);

    return (
        <main data-testid="checkout-page" className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-20">
            <button onClick={() => navigate(-1)} data-testid="checkout-back-button" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors hover:text-gold">
                <ArrowLeft size={14} /> Back
            </button>
            <h1 className="mt-8 font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl">
                Check<span className="italic text-espresso">out.</span>
            </h1>
            <div className="mt-12 grid grid-cols-1 gap-16 lg:grid-cols-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">01 — Where is it going?</p>
                    <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Full name</label>
                            <input value={form.name} onChange={set("name")} data-testid="checkout-name-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="Jane Doe" />
                        </div>
                        <div className="col-span-2">
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Street address</label>
                            <input value={form.street1} onChange={set("street1")} data-testid="checkout-street-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="123 Main Street" />
                        </div>
                        <div className="col-span-2">
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Apt / suite (optional)</label>
                            <input value={form.street2} onChange={set("street2")} data-testid="checkout-street2-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="Apt 2B" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">City</label>
                            <input value={form.city} onChange={set("city")} data-testid="checkout-city-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="Portland" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">State</label>
                                <input value={form.state} onChange={set("state")} data-testid="checkout-state-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="OR" />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">ZIP</label>
                                <input value={form.zip} onChange={set("zip")} data-testid="checkout-zip-input" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso" placeholder="97205" />
                            </div>
                        </div>
                        <div className="col-span-2">
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Country</label>
                            <select value={form.country} onChange={set("country")} data-testid="checkout-country-select" className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso">
                                {COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                            </select>
                        </div>
                    </div>
                    <button
                        onClick={getRates}
                        disabled={loadingRates}
                        data-testid="get-rates-button"
                        className="mt-8 flex items-center gap-3 border border-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-espresso hover:text-sand disabled:opacity-60"
                    >
                        <Truck size={14} />
                        {loadingRates ? "Asking USPS…" : "Calculate live USPS rates"}
                    </button>
                    {rates.length > 0 && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">02 — Choose your shipping</p>
                            <div className="mt-5 flex flex-col gap-3" data-testid="rates-list">
                                {rates.map((r, i) => (
                                    <button
                                        key={r.id}
                                        onClick={() => setSelectedRate(r)}
                                        data-testid={`rate-option-${i}`}
                                        className={`flex items-center justify-between border px-5 py-4 text-left transition-colors duration-300 ${selectedRate?.id === r.id ? "border-espresso bg-espresso text-sand" : "border-line bg-white hover:border-espresso"}`}
                                    >
                                        <span className="font-serif text-lg">{r.service}</span>
                                        <span className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.15em]">
                                            {r.estimated_days ? <span className={selectedRate?.id === r.id ? "text-sand/60" : "text-clay"}>{r.estimated_days} day{r.estimated_days === 1 ? "" : "s"}</span> : null}
                                            <span className="text-sm">${r.amount.toFixed(2)}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </motion.div>
                <motion.aside initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
                    <div className="border border-line bg-white p-8">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Your order</p>
                        <div className="mt-6 flex flex-col gap-5">
                            {items.map((i) => {
                                const key = itemKey(i.product.id, i.variant);
                                return (
                                <div key={key} className="flex items-center gap-4" data-testid={`checkout-item-${key}`}>
                                    <div className="h-16 w-12 shrink-0 overflow-hidden bg-line/40">
                                        <img src={imgSrc(itemImage(i))} alt="" className="h-full w-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-serif text-base leading-tight">{i.product.name}</p>
                                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                                            {i.variant ? `${i.variant.name} · ` : ""}× {i.quantity}
                                        </p>
                                    </div>
                                    <p className="font-mono text-sm">${(itemPrice(i) * i.quantity).toFixed(2)}</p>
                                </div>
                                );
                            })}
                        </div>
                        <div className="mt-8 border-t border-line pt-5 font-mono text-xs uppercase tracking-[0.15em]">
                            <div className="flex justify-between py-1.5"><span className="text-clay">Subtotal</span><span data-testid="checkout-subtotal">${total.toFixed(2)}</span></div>
                            <div className="flex justify-between py-1.5">
                                <span className="text-clay">Shipping</span>
                                <span data-testid="checkout-shipping">{selectedRate ? `$${selectedRate.amount.toFixed(2)}` : "—"}</span>
                            </div>
                            <div className="flex justify-between py-1.5"><span className="text-clay">Tax</span><span className="text-clay">at payment</span></div>
                            <div className="mt-3 flex justify-between border-t border-line pt-4 text-base normal-case tracking-normal">
                                <span className="font-serif text-xl">Total</span>
                                <span className="font-serif text-xl" data-testid="checkout-total">${grand.toFixed(2)}</span>
                            </div>
                        </div>
                        <button
                            onClick={pay}
                            disabled={paying || !selectedRate}
                            data-testid="pay-button"
                            className="group mt-8 flex w-full items-center justify-center gap-3 bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {paying ? "Redirecting…" : "Continue to secure payment"}
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                            <ShieldCheck size={12} /> Stripe · Live USPS rates · Label auto-purchased
                        </p>
                    </div>
                </motion.aside>
            </div>
        </main>
    );
}
